import { zodResolver } from '@hookform/resolvers/zod';
import { locationInputSchema, locationSchema, okSchema, type Location } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2, Pencil, Plus, Trash2, MapPin as MapPinIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { FormField } from '@/components/form-field';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiRequest } from '@/lib/api';
import { locationsQuery } from '@/lib/admin-queries';
import { applyServerErrors, errorMessage } from '@/lib/forms';

export const Route = createFileRoute('/_app/admin/locations')({ component: LocationsPage });

function LocationsPage() {
  const query = useQuery(locationsQuery);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Location | 'new' | null>(null);
  const remove = useMutation({
    mutationFn: (id: number) =>
      apiRequest(`/api/application/locations/${id}`, { method: 'DELETE', schema: okSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'locations'] }),
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <>
      <PageHeader
        title="Locations"
        icon={MapPinIcon}
        description="Group nodes by where they run, e.g. a room, a city or a provider."
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus /> New location
          </Button>
        }
      />
      <QueryState query={query}>
        {(locations) => (
          <div className="table-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Short code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Nodes</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-muted-foreground py-8 text-center">
                      No locations yet. Create one before adding a node.
                    </TableCell>
                  </TableRow>
                )}
                {locations.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="font-mono">{l.short}</TableCell>
                    <TableCell className="text-muted-foreground">{l.long ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.nodeCount}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${l.short}`}
                        onClick={() => setEditing(l)}
                      >
                        <Pencil />
                      </Button>
                      <Confirm
                        title={`Delete ${l.short}?`}
                        description="Locations with nodes can't be deleted."
                        action="Delete"
                        destructive
                        onConfirm={() => remove.mutate(l.id)}
                      >
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Delete ${l.short}`}
                          disabled={l.nodeCount > 0}
                        >
                          <Trash2 />
                        </Button>
                      </Confirm>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </QueryState>
      {editing && (
        <LocationDialog
          location={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

type Values = z.input<typeof locationInputSchema>;

function LocationDialog({ location, onClose }: { location: Location | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const form = useForm<Values>({
    resolver: zodResolver(locationInputSchema),
    defaultValues: { short: location?.short ?? '', long: location?.long ?? '' },
  });
  const save = useMutation({
    mutationFn: (body: Values) =>
      apiRequest(
        location ? `/api/application/locations/${location.id}` : '/api/application/locations',
        {
          method: location ? 'PATCH' : 'POST',
          body: { ...body, long: body.long || null },
          schema: locationSchema,
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'locations'] });
      onClose();
    },
    onError: (e) => applyServerErrors(e, form.setError),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{location ? 'Edit location' : 'New location'}</DialogTitle>
        </DialogHeader>
        <form
          id="location-form"
          className="grid gap-4"
          noValidate
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
        >
          <FormField
            label="Short code"
            error={form.formState.errors.short?.message}
            description="e.g. home, us-east"
          >
            <Input autoFocus {...form.register('short')} />
          </FormField>
          <FormField label="Description" error={form.formState.errors.long?.message}>
            <Input {...form.register('long')} />
          </FormField>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="location-form" disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
